/* eslint-disable @typescript-eslint/no-explicit-any */
import { useStaticQuery, graphql, Link as GatsbyLink } from 'gatsby';
import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useIntl } from 'gatsby-plugin-intl';
import { toggleCookieBanner, isQuebecUser } from "@pcf-engineering/gatsby-plugin-pcs-cookie-banner";
import { useEffect, useState } from 'react';
import parse from 'html-react-parser';

import useKeyValuePairs from '../hooks/useKeyValuePairs';

import RichText from './RichText';
import SectionContainer from './SectionContainer';
import InlineLink from './InlineLink';
import BladeController from './BladeController';
import BackToTop from './BackToTop';

const StyledFooter = styled.footer`
  background-color: ${themeGet('colors.greys.3')};

  @media only screen and (max-width: 679px) {
    padding-bottom: 70px;
  }
`;

const Container = styled(SectionContainer)`
  position: relative;
`;

const StyledFooterTop = styled.div`
  display: flex;
  flex-direction: column;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    flex-direction: row;
  }
`;

const Logo = styled.img`
  min-width: 127px;
  width: 100%;
  max-width: 127px;
  margin: 1rem 0;
`;

const FooterSocial = styled.div`
  padding-top: 20px;
  min-width: 140px;
`;

const SocialIcons = styled.div`
  display: flex;
  align-items: center;
`;

const SocialLink = styled.a`
  display: inline-block;
  padding: 0.7rem 1rem 0.7rem 0.4rem;

  &:first-child {
    padding: 0.7rem 1rem 0.7rem 0rem;
  }
`;

const SocialIcon = styled.img`
  max-height: 28px;
`;

const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 22px 0 0;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    width: 100%;
    justify-content: space-between;
    flex-wrap: nowrap;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    margin: 40px 0 0;
    padding: 0 0 0 8%;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    padding: 0 80px;
  }
`;

export const CookieSettingsButton = styled.button.attrs({ type: 'button' })`
  display: block;
  width: auto;
  color: ${themeGet('colors.white')};
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  text-align: left;
  &:hover {
    text-decoration: underline;
  }
`;

const LinkGroup = styled.div`
  flex: 0 0 50%;
  margin: 0 0 30px;
  padding: 0 8px;

  &:nth-last-child(-n + 2) {
    margin: 0;
  }

  &:nth-child(1n + 1) {
    padding: 0 0 0 8px;
  }

  &:nth-child(2n + 1) {
    padding: 0 8px 0 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    flex: 0 1 25%;
    margin: 0;

    &:nth-child(1n + 1),
    &:nth-child(2n + 1) {
      padding: 0 8px;
    }

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      padding-right: 0;
    }
  }

  @media only screen and (min-width: 768px) {
    &:nth-child(1n + 1),
    &:nth-child(2n + 1) {
      padding: 0 15px;
    }

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      padding-right: 0;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    flex: 0 1 auto;
  }
`;

const LinkStyles = css`
  display: block;
  width: auto;
  color: ${themeGet('colors.white')};
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  text-align: left;
  margin: 12px 0;

  &:hover {
    text-decoration: underline;
  }

  span {
    text-decoration: none;
  }
`;

const GroupLabel = styled.p`
  ${LinkStyles}
  font-weight: 600;
  font-size: ${themeGet('typography.desktop.body3.fontSize')};
  margin: 0 0 6px;
  &:hover {
    text-decoration: none;
  }
`;

const Link = styled(InlineLink)`
  ${LinkStyles}
`;

const BladeLink = styled(BladeController)`
  ${LinkStyles}
`;

const FirstLegalText = styled.div`
  padding: 40px 0px 30px;

  p {
    font-size: 12px;
    line-height: 16px;
    font-weight: normal;
    color: ${themeGet('colors.white')};
    margin: 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    padding: 50px 0px 30px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    padding: 50px 0px 35px;
  }
`;

const SecondLegalWrapper = styled.div`
  background-color: ${themeGet('colors.greys.4')};
`;

const SecondLegalText = styled(SectionContainer)`
  display: flex;
  flex-direction: column;
  padding: 40px 0px 30px;

  p {
    font-size: 12px;
    line-height: 16px;
    font-weight: normal;
    color: #b4b7bd;
    margin: 0 0 1rem 0;

    &:last-child {
      margin: 0;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    padding: 40px 0px 45px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    padding: 50px 0px 35px;
  }
`;

interface FooterProps {
  backToTop?: boolean;
  scrollFocusElement?: React.RefObject<HTMLElement>;
}

const Footer = ({
  backToTop = false,
  scrollFocusElement,
}: FooterProps) => {
  const data = useStaticQuery(query);
  const en = data.allContentfulFooter.nodes[0];
  const fr = data.allContentfulFooter.nodes[1];
  const intl = useIntl();
  const keyValuePairs = useKeyValuePairs(intl.locale);
  const isEnglish = intl.locale === 'en';
  const intlData = isEnglish ? en : fr;
  const [cookieBannerEnabled, setCookieBannerEnabled] = useState(false);

  useEffect(() => {
    setCookieBannerEnabled(process.env.GATSBY_PLUGIN_PCS_COOKIE_BANNER_ENABLED === "true");
  }, []);

  return (
    <StyledFooter>
      <Container>
        <StyledFooterTop>
          <FooterSocial>
            <GatsbyLink to="/">
              <Logo
                src={intlData.logoDam.asset.localImage.publicURL}
                alt={intlData.logoDam.altText}
              />
            </GatsbyLink>
            {isEnglish && (
              <SocialIcons>
                {intlData.iconsSocialMedia.map(
                  ({ id, iconImageDam, iconText, optionalLinkUrl }: any) => (
                    <SocialLink key={id} href={optionalLinkUrl}>
                      <SocialIcon
                        src={iconImageDam.asset.localImage.publicURL}
                        alt={iconText}
                      />
                    </SocialLink>
                  ),
                )}
              </SocialIcons>
            )}
            {intlData.accessibilityLink && (
              <SocialIcons>
                <SocialLink href={intlData.accessibilityLink.optionalLinkUrl}>
                  <SocialIcon
                    src={
                      intlData.accessibilityLink.iconImageDam.asset.localImage
                        .publicURL
                    }
                    alt={intlData.accessibilityLink.iconText}
                  />
                </SocialLink>
              </SocialIcons>
            )}
            {cookieBannerEnabled && isQuebecUser() && (
              <CookieSettingsButton
                id="ot-sdk-btn"
                type="button"
                onClick={toggleCookieBanner}
              >
                {keyValuePairs.COOKIE_SETTINGS}
              </CookieSettingsButton>
            )}
          </FooterSocial>
          <Links>
            {intlData.footerLinks.map(({ id, sectionHeading, links }: any) => (
              <LinkGroup key={id}>
                <GroupLabel>{sectionHeading}</GroupLabel>
                <div>
                  {links.map((link: any) => {
                    if (link.internal.type === 'ContentfulBlade') {
                      return <BladeLink key={link.id} data={link} />;
                    }
                    if (
                      link.linkText
                        && link.internal.type === 'ContentfulLinkWrapper'
                    ) {
                      return (
                        <Link key={link.id} href={link.linkUrl}>
                          {parse(link.linkText)}
                        </Link>
                      );
                    }
                    return null;
                  })}
                </div>
              </LinkGroup>
            ))}
          </Links>
        </StyledFooterTop>
        <FirstLegalText>
          <RichText document={intlData.firstLegalRTF} />
        </FirstLegalText>
      </Container>
      <SecondLegalWrapper>
        <SecondLegalText>
          <RichText document={intlData.secondLegalRTF} />
        </SecondLegalText>
      </SecondLegalWrapper>
      {backToTop && <BackToTop scrollFocusElement={scrollFocusElement} />}
    </StyledFooter>
  );
};

export default Footer;

const query = graphql`
  query FooterQueryV2 {
    allContentfulFooter {
      nodes {
        logoDam {
          ...DamAsset
        }
        iconsSocialMedia {
          id
          iconImageDam {
            ...DamAsset
          }
          iconText
          optionalLinkUrl
        }
        accessibilityLink {
          iconText
          iconTextRTF {
            raw
          }
          optionalLinkUrl
          iconImageDam {
            ...DamAsset
          }
        }
        footerLinks {
          id
          sectionHeading
          links {
            ...LinkWrapper
            ...Blade
          }
        }
        firstLegalRTF {
          raw
        }
        secondLegalRTF {
          raw
        }
      }
    }
  }
`;
